import { SectionCard } from "@/features/power_projects/components/ui";
import type { QuantitySummary, ZEElementType } from "../types";
import { fmtArea, fmtMeters } from "../utils";
import { ZEPresetKeyLegend } from "./ZEPresetKeyLegend";

interface Props {
  summary: QuantitySummary | undefined;
  dirty: boolean;
  elementTypes?: ZEElementType[];
}

/** Quantity rollup: metres of each line type, m² of each surface, count of each object
 *  type. While the drawing
 *  has unsaved edits, figures are the local estimate (≈, great-circle); after save they are
 *  the exact server values (EPSG:2180). */
export function ZEQuantitySummaryPanel({ summary, dirty, elementTypes }: Props) {
  const hasAny =
    summary &&
    (summary.lines.length > 0 ||
      summary.objects.length > 0 ||
      (summary.areas?.length ?? 0) > 0);
  const approx = dirty ? "≈ " : "";
  // The private-działki breakdown only makes sense against the saved drawing's discovered
  // parcels — never against the live (unsaved) estimate, whose parcels are stale.
  const showPrivate = !dirty && !!summary?.parcels_ready;

  return (
    <SectionCard title="Zestawienie ilości">
      {!hasAny ? (
        <p className="text-[11px] font-mono text-[var(--color-text-muted)]">
          Narysuj elementy, aby zobaczyć podsumowanie.
        </p>
      ) : (
        <div className="space-y-3">
          {summary!.lines.length > 0 && (
            <div>
              <p className="section-label mb-1">Linie</p>
              <ul className="space-y-0.5">
                {summary!.lines.map((l) => (
                  <li key={l.element_type} className="flex items-start justify-between gap-2 text-[12px] font-mono">
                    <span className="text-[var(--color-text-secondary)]">{l.label}</span>
                    <span className="text-right">
                      <span className="block text-[var(--color-text-primary)] font-bold">
                        {approx}
                        {fmtMeters(l.total_length_m)}
                      </span>
                      {showPrivate && (l.private_length_m ?? 0) > 0 && (
                        <span className="block text-[10px] leading-tight text-[var(--color-primary)]">
                          (w tym {fmtMeters(l.private_length_m)} na działkach prywatnych — {l.private_parcels} szt)
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(summary!.areas?.length ?? 0) > 0 && (
            <div>
              <p className="section-label mb-1">Powierzchnie (odtworzenie)</p>
              <ul className="space-y-0.5">
                {summary!.areas!.map((a) => (
                  <li key={a.element_type} className="flex items-start justify-between gap-2 text-[12px] font-mono">
                    <span className="text-[var(--color-text-secondary)]">{a.label}</span>
                    <span className="text-right">
                      <span className="block font-bold text-[var(--color-text-primary)]">
                        {approx}
                        {fmtArea(a.total_area_m2)}
                      </span>
                      {/* Obwód osobno — krawężnik rozlicza się po krawędzi, nie po polu. */}
                      {a.total_perimeter_m > 0 && (
                        <span className="block text-[10px] leading-tight text-[var(--color-text-tertiary)]">
                          obwód {approx}{fmtMeters(a.total_perimeter_m)}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {summary!.objects.length > 0 && (
            <div>
              <p className="section-label mb-1">Obiekty</p>
              <ul className="space-y-0.5">
                {summary!.objects.map((o) => (
                  <li key={o.element_type} className="flex items-start justify-between gap-2 text-[12px] font-mono">
                    <span className="text-[var(--color-text-secondary)]">{o.label}</span>
                    <span className="text-right">
                      <span className="block text-[var(--color-text-primary)] font-bold">
                        {o.count} {o.unit}
                      </span>
                      {showPrivate && (o.private_count ?? 0) > 0 && (
                        <span className="block text-[10px] leading-tight text-[var(--color-primary)]">
                          (w tym {o.private_count} na działkach prywatnych)
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {dirty && (
            <p className="text-[10px] font-mono text-amber-600 leading-snug">
              Wartości szacunkowe (≈) — zapisz rysunek, by przeliczyć dokładnie (EPSG:2180).
            </p>
          )}
        </div>
      )}
      {elementTypes && elementTypes.length > 0 && (
        <ZEPresetKeyLegend elementTypes={elementTypes} />
      )}
    </SectionCard>
  );
}
